import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, Check } from "lucide-react";

const LEVEL = { info: "bg-blue-500", success: "bg-emerald-500", attention: "bg-amber-500", urgent: "bg-red-500" };

export default function NotificationBell() {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const ref = useRef(null);
  const navigate = useNavigate();

  const loadCount = async () => { try { const { data } = await api.get("/notifications/unread-count"); setCount(data.count || 0); } catch { /* */ } };
  useEffect(() => { loadCount(); const t = setInterval(loadCount, 20000); return () => clearInterval(t); }, []);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);

  const toggle = async () => {
    const n = !open; setOpen(n);
    if (n) { try { setItems((await api.get("/notifications")).data.slice(0, 12)); } catch { /* */ } }
  };
  const openItem = async (it) => {
    try { if (!it.read) await api.post(`/notifications/${it.id}/read`); } catch { /* */ }
    setOpen(false); loadCount();
    if (it.link) navigate(it.link);
  };
  const markAll = async () => { await api.post("/notifications/read-all"); setItems(items.map((i) => ({ ...i, read: true }))); loadCount(); };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} data-testid="notification-bell" className="relative p-2 rounded-lg hover:bg-slate-100 text-slate-600">
        <Bell className="w-5 h-5" />
        {count > 0 && <span data-testid="notif-unread-badge" className="absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{count > 99 ? "99+" : count}</span>}
      </button>
      {open && (
        <div data-testid="notif-dropdown" className="absolute right-0 mt-2 w-80 max-w-[90vw] bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
            <span className="text-sm font-semibold text-slate-800">Notifications</span>
            <div className="flex items-center gap-2">
              <button onClick={markAll} className="text-xs text-emerald-600 hover:underline" data-testid="notif-markall">Tout lu</button>
              <button onClick={() => { setOpen(false); navigate("/notifications"); }} className="text-xs text-slate-500 hover:underline">Voir tout</button>
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? <div className="px-4 py-8 text-center text-sm text-slate-400">Aucune notification</div> :
              items.map((it) => (
                <button key={it.id} onClick={() => openItem(it)} data-testid={`notif-item-${it.id}`} className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 flex gap-3 ${it.read ? "opacity-60" : ""}`}>
                  <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${LEVEL[it.level] || "bg-slate-400"}`} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{it.title}</div>
                    {it.message && <div className="text-xs text-slate-500 line-clamp-2">{it.message}</div>}
                    <div className="text-[10px] text-slate-400 mt-0.5">{new Date(it.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</div>
                  </div>
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
